import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunks for fetching various reports
export const fetchStaffPerformance = createAsyncThunk(
  "report/fetchStaffPerformance",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.REPORT.STAFF_PERFORMANCE,
        { params },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch staff performance",
      );
    }
  },
);

export const fetchAnalytics = createAsyncThunk(
  "report/fetchAnalytics",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.REPORT.ANALYTICS, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch analytics",
      );
    }
  },
);

export const fetchStaffRatings = createAsyncThunk(
  "report/fetchStaffRatings",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.REPORT.STAFF_RATINGS,
        { params },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch staff ratings",
      );
    }
  },
);

export const fetchCustomerSummary = createAsyncThunk(
  "report/fetchCustomerSummary",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.REPORT.CUSTOMER_SUMMARY,
        { params },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer summary",
      );
    }
  },
);

export const fetchServiceUsage = createAsyncThunk(
  "report/fetchServiceUsage",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.REPORT.SERVICE_USAGE,
        { params },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch service usage",
      );
    }
  },
);

export const fetchEmployeeService = createAsyncThunk(
  "report/fetchEmployeeService",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.REPORT.EMPLOYEE_SERVICE,
        { params },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch employee service",
      );
    }
  },
);

const initialState = {
  staffPerformance: null,
  analytics: null,
  staffRatings: null,
  customerSummary: null,
  serviceUsage: null,
  employeeService: null,
  isLoading: false,
  error: null,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearReportError: (state) => {
      state.error = null;
    },
    clearReportData: (state) => {
      state.staffPerformance = null;
      state.analytics = null;
      state.staffRatings = null;
      state.customerSummary = null;
      state.serviceUsage = null;
      state.employeeService = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Staff Performance
      .addCase(fetchStaffPerformance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStaffPerformance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.staffPerformance = action.payload.data || action.payload || [];
      })
      .addCase(fetchStaffPerformance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Analytics
      .addCase(fetchAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload.data || action.payload || [];
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Staff Ratings
      .addCase(fetchStaffRatings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStaffRatings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.staffRatings = action.payload.data || action.payload || [];
      })
      .addCase(fetchStaffRatings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Customer Summary
      .addCase(fetchCustomerSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customerSummary = action.payload.data || action.payload || [];
      })
      .addCase(fetchCustomerSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Service Usage
      .addCase(fetchServiceUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serviceUsage = action.payload.data || action.payload || [];
      })
      .addCase(fetchServiceUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Employee Service
      .addCase(fetchEmployeeService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeService.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employeeService = action.payload.data || action.payload || [];
      })
      .addCase(fetchEmployeeService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReportError, clearReportData } = reportSlice.actions;

export const selectStaffPerformance = (state) => state.report.staffPerformance;
export const selectAnalytics = (state) => state.report.analytics;
export const selectStaffRatings = (state) => state.report.staffRatings;
export const selectCustomerSummary = (state) => state.report.customerSummary;
export const selectServiceUsage = (state) => state.report.serviceUsage;
export const selectEmployeeService = (state) => state.report.employeeService;
export const selectReportLoading = (state) => state.report.isLoading;
export const selectReportError = (state) => state.report.error;

export default reportSlice.reducer;
